import React from "react";
import Link from "next/link";

type ButtonBaseProps = {
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
};

type LinkHref = React.ComponentPropsWithoutRef<typeof Link>["href"];

type LinkExtraProps = Omit<React.ComponentPropsWithoutRef<typeof Link>, "href" | "className" | "style" | "children">;

type AnchorExtraProps = Omit<React.ComponentPropsWithoutRef<"a">, "href" | "className" | "style" | "children">;

type ButtonAsLinkProps = ButtonBaseProps & { href: LinkHref } & AnchorExtraProps & LinkExtraProps;

type ButtonAsButtonProps = ButtonBaseProps &
    React.ButtonHTMLAttributes<HTMLButtonElement> & {
        href?: never;
    };

type ButtonProps = ButtonAsLinkProps | ButtonAsButtonProps;

const Button: React.FC<ButtonProps> = (props) => {
    const paddingClasses = "px-[40px] py-[12px]";

    const baseClasses = `
    inline-block text-center cursor-pointer whitespace-nowrap
    rounded-[30px] text-lg leading-[17px] font-medium uppercase
    bg-blue text-white border-2 border-blue transition-none
    font-lato tracking-normal box-border visibility-visible
    hover:bg-white hover:text-blue hover:border-blue
`;

    if ("href" in props && props.href !== undefined) {
        const { href, children, className = "", style = {}, ...rest } = props;

        return (
            <Link
                href={href}
                className={`${baseClasses} ${paddingClasses} ${className}`}
                style={{
                    ...style,
                    textDecoration: "none",
                    boxShadow: "none",
                    outline: "none",
                }}
                {...rest}
            >
                {children}
            </Link>
        );
    }

    const { children, className = "", style = {}, ...rest } = props;

    return (
        <button
            className={`${baseClasses} ${paddingClasses} ${className}`}
            style={{
                ...style,
                textDecoration: "none",
                boxShadow: "none",
                outline: "none",
            }}
            {...rest}
        >
            {children}
        </button>
    );
};

export default Button;
